import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ContractorAuthService from '../../services/ContractorAuthService';

const ContractorInvite = ({ onRegister }) => {
  const { token } = useParams();
  const [inviteData, setInviteData] = useState(null);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    validateInvite();
  }, [token]);

  const validateInvite = async () => {
    try {
      const response = await ContractorAuthService.validateInvite(token);
      
      if (response.valid) {
        setInviteData({
          valid: true,
          contractorName: response.contractorName,
          email: response.email,
          expiresAt: response.expiresAt
        });
      } else {
        setError('Invalid or expired invitation');
      }
      
    } catch (error) {
      setError('Failed to validate invitation');
      console.error('Invite validation error:', error);
    } finally {
      setValidating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const registrationData = {
        InviteToken: token,
        Password: formData.password,
        ConfirmPassword: formData.password, // Add required field
        Phone: formData.phone
      };

      const response = await ContractorAuthService.register(registrationData);
      
      const success = response.success || response.Success;
      if (success) {
        // Store authentication data
        const token = response.token || response.Token;
        const user = response.user || response.User;
        localStorage.setItem('contractorToken', token);
        localStorage.setItem('contractorUser', JSON.stringify(user));
        
        // Redirect to contractor dashboard
        onRegister(user);
      } else {
        const message = response.message || response.Message;
        setError(message || 'Registration failed');
      }
      
    } catch (error) {
      setError('Registration failed. Please try again.');
      console.error('Contractor registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900">Invalid Invitation</h2>
            <p className="text-gray-600 mt-2">{error}</p>
            <Link 
              to="/contractor/login" 
              className="mt-4 inline-block text-blue-600 hover:text-blue-800 underline"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-16 w-auto flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="text-4xl text-blue-600">🏠</div>
                <div className="absolute -top-1 -right-1 text-lg text-green-500">🛡️</div>
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                ACVentures
              </div>
            </div>
            <div className="mt-1 text-xs text-gray-500 text-center">
              Contractor Portal
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Welcome, {inviteData?.contractorName}!
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Complete your registration to access your contractor portal
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="text-sm text-blue-800">
              <strong>Invitation Details:</strong>
              <br />Email: {inviteData?.email}
              <br />Expires: {inviteData?.expiresAt ? new Date(inviteData.expiresAt).toLocaleDateString() : 'N/A'}
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Create Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Minimum 6 characters"
              />
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm your password"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number (Optional)
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Account...' : 'Complete Registration'}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/contractor/login" className="text-blue-600 hover:text-blue-800 underline">
                Sign in here
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContractorInvite;

